import type * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useParams, Outlet } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'
import { clsx } from 'clsx'

import {
  EmptyState,
  CriticalIcon,
  PageLayout,
  Text,
  useDelayedLoading,
  useLocalStorage,
  useSidebar,
} from '@buffer-mono/popcorn'

import { ALL_CHANNELS_TIMEZONE_STORAGE_KEY } from '~publish/components/AllChannelsTimeZoneSelect'
import { ChannelError } from '~publish/components/ChannelError'
import { PageNotices } from '~publish/components/PageNotices'
import { RegisteredBannersProvider } from '~publish/components/RegisteredBanner'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import useMatchCurrentOrParentPath from '~publish/hooks/useMatchCurrentOrParentPath'
import { useTimeFormatPreference } from '~publish/hooks/useTimeFormatPreference'
import { TimezoneProvider } from '~publish/hooks/useTimezone'
import { newCalendarSingleChannel } from '~publish/legacy/routes'

import { canPerform } from '~publish/utils/permissions/canPerform'
import {
  areAllChannelsRestrictedForPublish,
  areAllChannelsViewOnlyForPublish,
} from '~publish/utils/permissions/viewOnlyPublish'

import { AllChannelsAccessDenied } from '../AllChannels/AllChannelsAccessDenied'
import { AllChannelsPausedQueueNotice } from '../AllChannels/AllChannelsPausedQueueNotice'
import { ChannelPageNotifications, GetChannelInfo } from '../Channel'
import { ChannelAccessDenied } from '../Channel/ChannelAccessDenied'
import { Calendar } from './Calendar'
import { ChannelProvider } from '../Channel/ChannelContext'
import { CalendarHeader } from './CalendarHeader/CalendarHeader'
import { CalendarUndatedDraftsSidebar } from './CalendarUndatedDraftsSidebar'
import { canShowMultiChannelCalendarSlots } from './hooks/useCalendarSlots'
import { useCalendarState } from './hooks/useCalendarState'
import { useUndatedDraftsCount } from './useUndatedDraftsCount'

import calendarWorkspaceStyles from '~publish/components/Calendar/CalendarWorkspace.module.css'

import styles from './CalendarPage.module.css'

function SingleChannelProvider({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  const organizationId = useOrganizationId()
  const id = useParams<{ id: string }>().id

  const { data, error } = useQuery(GetChannelInfo, {
    variables: {
      channelId: id ?? '',
    },
    skip: !organizationId,
  })

  if (error) {
    return <ChannelError error={error} />
  }

  const channel = data?.channel
  if (!channel) {
    return <></>
  }
  // Same gate as the single-channel list route (@see ChannelPage): this
  // calendar route mounts directly, so a deep link to a channel the actor
  // can read but not view in Publish must show the access state rather than
  // an empty calendar.
  if (!canPerform(channel, 'viewPublish')) {
    return <ChannelAccessDenied channel={channel} />
  }
  return (
    <ChannelProvider channel={channel}>
      <TimezoneProvider timeZone={channel.timezone}>
        <RegisteredBannersProvider
          key={`registered-banner-provider-${channel.name}-calendar`}
        >
          {children}
        </RegisteredBannersProvider>
      </TimezoneProvider>
    </ChannelProvider>
  )
}

function CalendarProviders({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  const isSingleChannel = useMatchCurrentOrParentPath(
    newCalendarSingleChannel.route,
  )
  const [allChannelsTimeZone] = useLocalStorage(
    ALL_CHANNELS_TIMEZONE_STORAGE_KEY,
    CURRENT_TIME_ZONE,
  )
  if (isSingleChannel) {
    return <SingleChannelProvider>{children}</SingleChannelProvider>
  }

  return (
    <TimezoneProvider timeZone={allChannelsTimeZone}>
      <RegisteredBannersProvider
        key={`registered-notice-provider-allChannels-calendar`}
      >
        {children}
      </RegisteredBannersProvider>
    </TimezoneProvider>
  )
}

const UNDATED_DRAFTS_SIDEBAR_ID = 'calendar-undated-drafts-sidebar'

const CalendarPageContent = (): JSX.Element => {
  const organizationId = useOrganizationId()
  const calendarState = useCalendarState()
  const [isUndatedDraftsOpen, setIsUndatedDraftsOpen] = useState(false)
  const [hasOpenedUndatedDrafts, setHasOpenedUndatedDrafts] = useState(false)
  const { open: isAppSidebarOpen, setOpen: setAppSidebarOpen } = useSidebar()
  const wasAppSidebarOpenRef = useRef(isAppSidebarOpen)
  const calendarWorkspaceRef = useRef<HTMLDivElement>(null)

  const { filters, postsQuery } = calendarState
  const showCalendarLoadingProgress = useDelayedLoading(postsQuery.loading, {
    delay: 0,
    minDuration: 400,
  })
  const {
    channelsFilter,
    tagsFilter,
    channels,
    disabledChannelIds,
    viewOnlyChannelIds,
    onboardingChannelFocus,
    error,
    channelsLoading,
    isSingleChannel,
    postTypesFilter,
  } = filters

  const timeFormatPreference = useTimeFormatPreference()
  const hasTwentyFourHourTimeFormat = timeFormatPreference === '24'

  // Single channel keys off that channel; All Channels keys off the whole
  // set, so a mixed-access user keeps the editing affordances (per-post
  // restrictions live on the cards). The badge additionally requires at least
  // one viewable channel — a fully restricted set renders the access-denied
  // treatment instead.
  const selectedChannels = isSingleChannel
    ? channels.filter((channel) =>
        channelsFilter.sanitized.includes(channel.id),
      )
    : channels
  const slotChannels = useMemo(() => {
    const channelsInScope = channelsFilter.sanitized.length
      ? channels.filter((channel) =>
          channelsFilter.sanitized.includes(channel.id),
        )
      : channels

    return channelsInScope.filter((channel) =>
      canPerform(channel, 'manageUpdates'),
    )
  }, [channels, channelsFilter.sanitized])
  const viewOnly =
    selectedChannels.length > 0 &&
    !selectedChannels.some((channel) => canPerform(channel, 'manageUpdates'))
  const showViewOnlyBadge = areAllChannelsViewOnlyForPublish(selectedChannels)
  const showPostingTimesControl =
    !viewOnly &&
    (isSingleChannel ||
      (channelsFilter.sanitized.length === 1 && slotChannels.length === 1) ||
      canShowMultiChannelCalendarSlots({
        channelsLoading,
        viewOnly,
      }))

  const pausedChannels = channels.filter((channel) => channel.isQueuePaused)
  const undatedDraftsCount = useUndatedDraftsCount({
    organizationId,
    channelIds: channelsFilter.queryParams,
    tagIds: tagsFilter.queryParams,
  })
  const isUndatedDraftsSidebarMounted =
    hasOpenedUndatedDrafts || isUndatedDraftsOpen
  const toggleUndatedDrafts = useCallback((): void => {
    if (!isUndatedDraftsOpen) {
      setAppSidebarOpen(false)
    }
    setIsUndatedDraftsOpen(!isUndatedDraftsOpen)
  }, [isUndatedDraftsOpen, setAppSidebarOpen])

  useEffect(() => {
    if (isAppSidebarOpen && !wasAppSidebarOpenRef.current) {
      setIsUndatedDraftsOpen(false)
    }
    wasAppSidebarOpenRef.current = isAppSidebarOpen
  }, [isAppSidebarOpen])

  useEffect(() => {
    if (isUndatedDraftsOpen) {
      setHasOpenedUndatedDrafts(true)
    }
  }, [isUndatedDraftsOpen])
  const closeUndatedDrafts = useCallback((): void => {
    setIsUndatedDraftsOpen(false)
  }, [])

  if (error) {
    return (
      <EmptyState size="medium" variant="critical">
        <EmptyState.Icon>
          <CriticalIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Failed to load</EmptyState.Heading>
        <EmptyState.Description>
          Error happened, please let our team know about it.{' '}
          <Text color="critical">{error.message}</Text>
        </EmptyState.Description>
      </EmptyState>
    )
  }

  // The single-channel calendar is gated upstream (@see SingleChannelProvider),
  // so this only guards the All Channels calendar.
  if (!isSingleChannel && areAllChannelsRestrictedForPublish(channels)) {
    return <AllChannelsAccessDenied />
  }

  return (
    <PageLayout className={styles.layout}>
      <PageNotices>
        {isSingleChannel ? (
          <ChannelPageNotifications />
        ) : (
          pausedChannels.length > 0 && (
            <AllChannelsPausedQueueNotice pausedChannels={pausedChannels} />
          )
        )}
      </PageNotices>
      <CalendarHeader
        isSingleChannel={isSingleChannel}
        selectedTagIds={tagsFilter.sanitized}
        onSelectTags={tagsFilter.onSelect}
        postTypeOptions={postTypesFilter.options}
        selectedPostTypes={postTypesFilter.selected}
        onSelectPostTypes={postTypesFilter.setSelected}
        selectedChannelIds={channelsFilter.sanitized}
        disabledChannelIds={disabledChannelIds}
        viewOnlyChannelIds={viewOnlyChannelIds}
        channels={channels}
        onSelectChannels={channelsFilter.onSelect}
        viewOnly={viewOnly}
        showPostingTimesControl={showPostingTimesControl}
        showViewOnlyBadge={showViewOnlyBadge}
        isUndatedDraftsOpen={isUndatedDraftsOpen}
        isUndatedDraftsSidebarMounted={isUndatedDraftsSidebarMounted}
        undatedDraftsSidebarId={UNDATED_DRAFTS_SIDEBAR_ID}
        undatedDraftsCount={undatedDraftsCount}
        onToggleUndatedDrafts={toggleUndatedDrafts}
      />

      <PageLayout.Container size="full" className={styles.calendarContainer}>
        <div
          ref={calendarWorkspaceRef}
          className={clsx(
            calendarWorkspaceStyles.workspace,
            isUndatedDraftsOpen && calendarWorkspaceStyles.expanded,
          )}
        >
          <Calendar
            channelCount={channels.length}
            className={calendarWorkspaceStyles.pane}
            dragBoundaryRef={calendarWorkspaceRef}
            hasTwentyFourHourTimeFormat={hasTwentyFourHourTimeFormat}
            channelsLoading={channelsLoading}
            onboardingServices={onboardingChannelFocus}
            selectedChannelIds={channelsFilter.sanitized}
            showLoadingProgress={showCalendarLoadingProgress}
            slotChannels={slotChannels}
            postsQuery={postsQuery}
            viewOnly={viewOnly}
          >
            {isUndatedDraftsSidebarMounted && (
              <CalendarUndatedDraftsSidebar
                id={UNDATED_DRAFTS_SIDEBAR_ID}
                isOpen={isUndatedDraftsOpen}
                isSchedulingDisabled={viewOnly}
                channelIds={channelsFilter.queryParams}
                tagIds={tagsFilter.queryParams}
                onClose={closeUndatedDrafts}
              />
            )}
          </Calendar>
        </div>
      </PageLayout.Container>
    </PageLayout>
  )
}

function CalendarPage(): JSX.Element {
  return (
    <CalendarProviders>
      <CalendarPageContent />
      {/* Outlet for modals and dialogs */}
      <Outlet />
    </CalendarProviders>
  )
}

export default CalendarPage
